import { Inject, Injectable, Optional } from '@angular/core';
import { AbstractControlOptions, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, startWith } from 'rxjs/operators';
import _difference from 'lodash-es/difference';
import { FormField, FormFieldSerializedValues } from '@shared/shared/form-fields';
import { FormFieldsService } from '@shared/shared/services/form-fields.service';
import { FORM_OPTIONS } from '@shared/shared';

interface SetFieldsOptions {
  forceUpdateValues?: boolean;
  deleteOldFields?: boolean;
}

@Injectable()
export class FormFieldsInstanceService {
  private form: UntypedFormGroup;
  private fieldControlsInitValues: Map<string, unknown> = new Map<string, unknown>();
  private fieldControls: FormField[] = [];
  private handleErrorsSubscriptions = new Map<string, Subscription>();

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly formFieldsService: FormFieldsService,
    @Optional() @Inject(FORM_OPTIONS) private readonly formOptions: AbstractControlOptions,
  ) {
    this.createEmptyForm();
  }

  getForm(): UntypedFormGroup {
    return this.form;
  }

  serializeValues(): FormFieldSerializedValues {
    return this.formFieldsService.serializeValues(this.form, this.fieldControls);
  }

  setFields(fieldControls: FormField[], { forceUpdateValues, deleteOldFields }: SetFieldsOptions = {}): void {
    if (deleteOldFields) {
      this.createEmptyForm();
      // TODO don't allow multiple setFields() calls OR add better support (unsubscribe, etc.)
    }

    this.fieldControls = fieldControls;
    this.fieldControls.forEach((field: FormField) => {
      this.addControl(field);
      this.updateControlValue(field, { forceUpdateValues });
      this.addErrorsHandler(field);
    });

    this.removeOldControls(fieldControls);
  }

  setError(fieldKey: string, error: string): void {
    const field = this.fieldControls.find(({ key }) => key === fieldKey);
    if (!field) {
      throw new ReferenceError(`FormFieldsInstanceService.setError ${fieldKey} not found`);
    }

    const control = this.form.get(field.key);
    control.setErrors({ backend: error });
    control.markAsTouched();
    control.updateValueAndValidity();
  }

  clearError(fieldKey: string): void {
    const field = this.fieldControls.find(({ key }) => key === fieldKey);
    if (!field) {
      throw new ReferenceError(`FormFieldsInstanceService.setError ${fieldKey} not found`);
    }

    const allErrors = this.form.get(field.key).errors;
    this.form.get(field.key).setErrors(null);

    if (!allErrors) {
      return;
    }

    const errorsWithoutBackend = Object.entries(allErrors).reduce((all, [key, val]) => {
      if (key === 'backend') {
        return all;
      }

      return {
        ...all,
        [key]: val,
      };
    }, null);

    this.form.get(field.key).setErrors(errorsWithoutBackend);
  }

  private createEmptyForm(): void {
    this.form = new UntypedFormGroup({}, this.formOptions);
  }

  private addControl(field: FormField): void {
    const hasControl = !!this.form.get(field.key);
    if (!hasControl) {
      this.form.addControl(field.key, this.formFieldsService.getFormControl(field));
    }
  }

  private updateControlValue(
    field: FormField,
    { forceUpdateValues = false }: { forceUpdateValues?: boolean } = {},
  ): void {
    const isUpdate = this.fieldControlsInitValues.has(field.key);

    if (isUpdate) {
      const isFieldValueChanged =
        this.fieldControlsInitValues.get(field.key) !== field.value ||
        (forceUpdateValues && this.form.get(field.key).value !== field.value);

      if (isFieldValueChanged) {
        this.formFieldsService.updateFormControlValue(this.form.get(field.key), field);
      }
    }

    this.fieldControlsInitValues.set(field.key, field.value);
  }

  private removeOldControls(formFields: FormField[]): void {
    _difference(
      Object.keys(this.form.controls),
      formFields.map(field => field.key),
    ).forEach((fieldKey: string) => {
      this.form.removeControl(fieldKey);
      this.fieldControlsInitValues.delete(fieldKey);
      this.removeErrorsHandler(fieldKey);
    });
  }

  private addErrorsHandler(field: FormField): void {
    if (this.handleErrorsSubscriptions.has(field.key)) {
      return;
    }

    const sub = this.form
      .get(field.key)
      .valueChanges.pipe(startWith(field.value || null), distinctUntilChanged())
      .subscribe(() => {
        if (this.form.get(field.key)?.errors) {
          this.clearError(field.key);
        }
      });

    this.handleErrorsSubscriptions.set(field.key, sub);
  }

  private removeErrorsHandler(fieldKey: string): void {
    if (this.handleErrorsSubscriptions.has(fieldKey)) {
      const sub = this.handleErrorsSubscriptions.get(fieldKey);

      sub.unsubscribe();
      this.handleErrorsSubscriptions.delete(fieldKey);
    }
  }
}
